<template>
    <div class="row">
        <div class="d-flex justify-content-between align-items-center gap-2 mb-2">
            <label for="input" class="form-label mb-0">Please choose a small subset of data to be analyzed.
                (CSV/JSON/AVRO/PARQUET less than 5 MB)</label>
            <div class="d-flex justify-content-end align-items-center gap-2">
                <span class="small text-muted fw-bold text-nowrap align-items-center">Job Type</span>
                <select type="select" class="form-select form-select-sm w-auto" v-model="mode" :disabled="loading">
                    <option value="mapper">Mapper</option>
                    <option value="pyspark">PySpark</option>
                    <option value="dbt">DBT</option>
                </select>
            </div>
        </div>

        <div class="col">
            <BrowseFile v-model="fileData" :acceptedFileTypes="['csv', 'json', 'avro', 'parquet']"
                :maxFileSize="5242880" @input="handleFileDataUpdate" />
        </div>
        <div class="col-2">
            <button class="btn btn-primary w-100" :disabled="!fileData.isFileSelected || loading" @click="submitAMA">
                <div v-if="loading" class="text-center">
                    <div class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <span v-else>Map My Data</span>
            </button>
        </div>
        <div class="col-2"
            v-if="mode == 'mapper' && response && response.files && $store.state.activeTab == 'Mapping Config'">
            <button class="btn btn-primary w-100"
                @click="showModal({ component: 'addJob', data: { files: configFiles, screenlarge: true } })">
                <span>Create Job</span>
            </button>
        </div>
        <div class="small text-muted text-center mt-2">This tool uses AI, which can make mistakes. Verify important outputs before acting on them.</div>
    </div>
    <div class="mb-3 mt-3 custom-instr-container">
        <div class="d-flex align-items-center justify-content-between p-2 px-3 custom-instr collapsed" data-bs-toggle="collapse" data-bs-target="#customConfigCollapse" 
            aria-expanded="false" aria-controls="customConfigCollapse">
            <div class="d-flex align-items-center gap-2">
                <span class="fw-semibold text-dark me-1">Custom Configurations</span>
                <i class="fa-solid fa-chevron-down text-muted custom-collapse-arrow-down" style="font-size: 0.75rem; margin-top: 0.1rem;"></i>
                <i class="fa-solid fa-chevron-up text-muted custom-collapse-arrow-up" style="font-size: 0.75rem; margin-top: 0.15rem;"></i>
            </div>
            <div>
                <span v-if="custom_instructions" class="custom-config-badge fs-5">
                    <i class="fa-solid fa-circle-check"></i>
                    Custom Instructions
                </span>
                <span v-if="custom_schema" class="custom-config-badge fs-5 ms-2">
                    <i class="fa-solid fa-circle-check"></i>
                    Custom Schema
                </span>
            </div>
        </div>

        <div class="collapse" id="customConfigCollapse">
            <div class="p-3">
                <div class="form-group fs-5 inline-field row me-2" rows="4">
                    <label class="col-sm-3 col-form-label fs-5">
                        <span class="me-1">Custom Instructions</span>
                        <i class="fa-solid fa-circle-info text-muted" data-bs-toggle="tooltip" data-bs-placement="right" title="Add custom instructions"></i>
                    </label>
                    <textarea class="form-control col fs-5" type="text" rows="3" v-model="custom_instructions" :disabled="loading" :readonly="loading"
                        placeholder="Add any custom instructions for the agent here. For example, you can provide additional context about the data.">
                    </textarea>
                </div>
                <div class="form-group fs-5 inline-field row me-2">
                    <label class="col-sm-3 col-form-label fs-5">
                        <span class="me-1">Custom Schema</span>
                        <i class="fa-solid fa-circle-info text-muted" data-bs-toggle="tooltip" data-bs-placement="right" title="Add a custom schema for the mapping process"></i>
                    </label>
                    <CodemirrorEditor class="form-control col fs-5" :editorId="`mmd-custom-configuration`" v-model="custom_schema" mode="yaml"
                        :refreshOn="loading" :disabled="loading" :readOnly="loading" :performLint="false" :maxHeight="'200px'">
                    </CodemirrorEditor>
                </div>
            </div>
        </div>
    </div>
    <tabs :tabs="tabItems" :btnTabs="true" v-if="submitted">
        <template v-slot:audit>
            <div class="mb-3">
                <div v-if="auditReport" class="card">
                    <div class='card-body markdown-content custom-class'>
                        <Markdown :content="auditReport" />
                    </div>
                </div>
                <div v-else-if="auditError" :class="['alert', `alert-${auditErrorClass}`]">
                    {{ auditError }}
                </div>
                <div v-else-if="auditLoading || loading" class="card card-body">
                    <div class="mt-3 ms-3 ps-1">
                        <div class="text-center py-2">
                            <div class="spinner-border mb-2" role="status" style="width: 1.5em; height: 1.5em; opacity: 0.5;">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <p class="text-muted mb-0">Auditing Data...</p>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:mapper>
            <div id="">
                <div v-if="mapperStatusList?.length > 0 || mapperLoadingStatus == 'running'" class="mt-2 mb-3">
                    <div class="mapper-status collapsed" role="button" data-bs-toggle="collapse" :data-bs-target="'#mapperStatusCollapse'">
                        <i v-if="mapperLoadingStatus == 'running'" class="fa-solid fa-circle fa-beat-fade text-success fs-5"></i>
                        <i v-else class="fa-solid fa-check text-success"></i>
                        <span class="status-text text-muted fst-italic fw-bold"> {{ getCurrentMapperStatus }} </span>
                        <!-- Conditionally display any one icon based on collapsed -->
                        <i class="fa-solid fa-angle-down text-muted mapper-collapse-arrow-down" style="font-size: 0.75rem; margin-top: 0.15rem;"></i>
                        <i class="fa-solid fa-angle-up text-muted mapper-collapse-arrow-up" style="font-size: 0.75rem; margin-top: 0.15rem;"></i>
                    </div>
                    <div class="status-text text-muted mt-2 collapse" :id="'mapperStatusCollapse'">
                        <div class="list-unstyled">
                            <div v-for="(status, index) in mapperStatusList" :key="index" class="row py-2 mx-0 border-bottom">
                                <div class="col-2 px-0 pe-2 d-flex align-items-top" style="word-break: normal;">
                                    <span class="d-inline-flex justify-content-center me-2" style="width: 1.3rem;">
                                        <div>
                                            <i :class="getAgentStatusIcon(status.label, streaming = (mapperLoadingStatus == 'running' && currentStatus(mapperStatusList)?.[index] == true))"></i>
                                        </div>
                                    </span>
                                    <span class="fw-bold">{{ status.label }}</span>
                                </div>
                                <div class="col-8 px-0">
                                    <span>{{ status.detail }}</span>
                                </div>
                                <div class="col-2 text-end px-0">
                                    <span>{{ status.timestamp }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="mapperError" class="alert alert-danger mb-3">
                    {{ mapperError }}
                </div>
                <div class="row">
                    <div class="col-4">
                        <div class="card">
                            <div class='custom-class'>
                                <div v-if="textResponse" class="card-body">
                                    <div class="float-end">
                                        <button class="btn btn-primary btn-sm" @click="showModal({ component: 'mapperDetailView', data: { response: response, screenlarge: true } })">
                                            <i class="fa fa-up-right-and-down-left-from-center"></i>
                                        </button>
                                    </div>
                                    <Markdown :content="textResponse" />
                                </div>
                                <div v-else>
                                    <div class="mt-3 ms-3 ps-1">
                                        <div class="text-center py-2">
                                            <div v-if="mapperLoadingStatus == 'running'" class="spinner-border mb-2" role="status" style="width: 1.5em; height: 1.5em; opacity: 0.5;">
                                                <span class="visually-hidden">Loading...</span>
                                            </div>
                                            <i v-else-if="mapperLoadingStatus == 'waiting'" class="fa-solid fa-hourglass-half text-muted mb-2" style="font-size: 1.5rem; opacity: 0.5;"></i>
                                            <i v-else class="fa-solid fa-circle-exclamation text-muted mb-2" style="font-size: 1.5rem; opacity: 0.5;"></i>

                                            <p v-if="mapperLoadingStatus == 'running'" class="text-muted mb-0">Generating Analysis...</p>
                                            <p v-else-if="mapperLoadingStatus == 'waiting'" class="text-muted mb-0">Awaiting next step...</p>
                                            <p v-else class="text-muted mb-0">No data found...</p>
                                        </div>
                                    </div>
                                    <div :class="[$store.state.darkMode ? 'loading-content-card-dark' : 'loading-content-card']">
                                        <div class="content">
                                            <h2></h2>
                                            <p></p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-8">
                        <div v-if="!response || !response.files" class="card">
                            <div class="ms-3 ps-1">
                                <div class="mt-3 ms-3 ps-1">
                                    <div class="text-center py-2">
                                        <div v-if="mapperLoadingStatus == 'running'" class="spinner-border mb-2" role="status" style="width: 1.5em; height: 1.5em; opacity: 0.5;">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                        <i v-else-if="mapperLoadingStatus == 'waiting'" class="fa-solid fa-hourglass-half text-muted mb-2" style="font-size: 1.5rem; opacity: 0.5;"></i>
                                        <i v-else class="fa-solid fa-circle-exclamation text-muted mb-2" style="font-size: 1.5rem; opacity: 0.5;"></i>

                                        <p v-if="mapperLoadingStatus == 'running'" class="text-muted mb-0">
                                            {{ mode == 'mapper' ? 'Creating Mapping Configuraton...' : mode == 'pyspark' ? 'Creating PySpark Configuraton...' : 'Creating DBT Configuraton...' }}
                                        </p>
                                        <p v-else-if="mapperLoadingStatus == 'waiting'" class="text-muted mb-0">Awaiting next step...</p>
                                        <p v-else class="text-muted mb-0">No data found...</p>
                                    </div>
                                </div>
                            </div>
                            <div :class="[$store.state.darkMode ? 'loading-content-card-dark' : 'loading-content-card']">
                                <div class="content">
                                    <h2></h2>
                                    <p></p>
                                </div>
                            </div>
                        </div>
                        <job-editor v-model="yamlContent" :activeTab="activeTab" :customHeight="'100%'"></job-editor>
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:advisor>
            <div id="">
                <div v-if="advisorStatusList?.length > 0 || advisorLoadingStatus == 'running'" class="mt-2 mb-3">
                    <div class="advisor-status collapsed" role="button" data-bs-toggle="collapse" :data-bs-target="'#advisorStatusCollapse'">
                        <i v-if="advisorLoadingStatus == 'running'" class="fa-solid fa-circle fa-beat-fade text-success fs-5"></i>
                        <i v-else class="fa-solid fa-check text-success"></i>
                        <span class="status-text text-muted fst-italic fw-bold"> {{ getCurrentAdvisorStatus }} </span>
                        <!-- Conditionally display any one icon based on collapsed -->
                        <i class="fa-solid fa-angle-down text-muted advisor-collapse-arrow-down" style="font-size: 0.75rem; margin-top: 0.15rem;"></i>
                        <i class="fa-solid fa-angle-up text-muted advisor-collapse-arrow-up" style="font-size: 0.75rem; margin-top: 0.15rem;"></i>
                    </div>
                    <div class="status-text text-muted mt-2 collapse" :id="'advisorStatusCollapse'">
                        <div class="list-unstyled">
                            <div v-for="(status, index) in advisorStatusList" :key="index" class="row py-2 mx-0 border-bottom">
                                <div class="col-2 px-0 pe-2 d-flex align-items-top" style="word-break: normal;">
                                    <span class="d-inline-flex justify-content-center me-2" style="width: 1.3rem;">
                                        <div>
                                            <i :class="getAgentStatusIcon(status.label, streaming = (advisorLoadingStatus == 'running' && currentStatus(advisorStatusList)?.[index] == true))"></i>
                                        </div>
                                    </span>
                                    <span class="fw-bold">{{ status.label }}</span>
                                </div>
                                <div class="col-8 px-0">
                                    <span>{{ status.detail }}</span>
                                </div>
                                <div class="col-2 text-end px-0">
                                    <span>{{ status.timestamp }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="advisorError" class="alert alert-danger mb-3">
                    {{ advisorError }}
                </div>
                <div v-else-if="!advisorResponse" class="card">
                    <div class="mt-3 ms-3 ps-1">
                        <div class="mt-3 ms-3 ps-1">
                            <div class="text-center py-2">
                                <div v-if="advisorLoadingStatus == 'running'" class="spinner-border mb-2" role="status" style="width: 1.5em; height: 1.5em; opacity: 0.5;">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                                <i v-else-if="advisorLoadingStatus == 'waiting'" class="fa-solid fa-hourglass-half text-muted mb-2" style="font-size: 1.5rem; opacity: 0.5;"></i>
                                <i v-else class="fa-solid fa-circle-exclamation text-muted mb-2" style="font-size: 1.5rem; opacity: 0.5;"></i>

                                <p v-if="advisorLoadingStatus == 'running'" class="text-muted mb-0">Analyzing Data Mart Solutions...</p>
                                <p v-else-if="advisorLoadingStatus == 'waiting'" class="text-muted mb-0">Awaiting next step...</p>
                                <p v-else class="text-muted mb-0">No data found...</p>
                            </div>
                        </div>
                    </div>
                    <div :class="[$store.state.darkMode ? 'loading-content-card-dark' : 'loading-content-card']">
                        <div class="content">
                            <h2></h2>
                            <p></p>
                        </div>
                    </div>
                </div>
                <div v-else class="card">
                    <div class='card-body markdown-content custom-class'>
                        <Markdown :content="advisorResponse"></Markdown>
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:graph>
            <div id="">
                <div class="card">
                    <div class='card-body markdown-content'>
                        <div ref="graphContainer" class="graph-container d-flex justify-content-center"></div>
                    </div>
                </div>
            </div>
        </template>
    </tabs>
</template>

<script>
import axios from "axios";
import { nextTick } from "vue";
import untruncateJson from "untruncate-json";
import JobEditor from "../../components/sandbox/JobEditor.vue";
import Tabs from "../../components/Tabs.vue";
import CodemirrorEditor from "../../components/CodemirrorEditor.vue";
import Markdown from "../../components/Markdown.vue";
import BrowseFile from "../../components/BrowseFile.vue";
import * as d3 from "d3";
import { mapActions, mapState } from "vuex";
import { v4 as uuidv4 } from 'uuid';
import { getAgentsRequestQueryParams, mergeAgentsQueryIntoUrl } from '@/utils/agentsApi';
import { getAgentStatusIcon, currentStatus } from "@/utils/commonFunction";
export default {
    components: { JobEditor, Tabs, Markdown, CodemirrorEditor, BrowseFile },
    data() {
        return {
            submitted: false,
            fileContent: null,
            loading: false,
            auditLoading: false,
            auditError: "",
            auditErrorClass: "danger",
            mapperLoadingStatus: 'waiting',
            mapperError: "",
            advisorLoadingStatus: 'waiting',
            advisorError: "",
            activeTab: "",
            response: "",
            eventSource: null,
            isCSV: true,
            auditReport: "",
            advisorResponse: "",
            graphResponse: "",
            configFiles: [],
            mode: "mapper",
            showCreateJobButton: true,
            fileType: "",
            fileData: {
                s3Path: '',
                presignedUrl: null,
                sandbox: null,
                localFile: null,
                fileContent: null,
                pathError: '',
                isFileSelected: false
            },
            outputParserData: null,
            custom_instructions: "",
            custom_schema: "",
            mapperMessageId: null,
            resourceId: null,

            mapperStatus: null,
            mapperStatusList: [],
            advisorStatus: null,
            advisorStatusList: [],
        }
    },
    watch: {
        "response.files": {
            handler: function (newFiles) {
                //change key from name to relative_path
                if (newFiles && newFiles.length > 0) {
                    this.configFiles = newFiles.map((file) => {
                        return {
                            relative_path: file.name,
                            content: file.content
                        }
                    });
                } else {
                    this.configFiles = [];
                }
            },
            deep: true
        },
        '$store.state.darkMode': {
            handler(newVal) {
                if (this.graphResponse) {
                    this.drawGraph(this.graphResponse)
                }
            }
        }
    },
    computed: {
        ...mapState({
            currentClient: state => state.currentClient,
            accessToken: state => state.accessToken,
        }),
        textResponse() {
            if (this.response?.text_response)
                return (this.response.text_response || this.response.textResponse)
            return null
        },
        yamlContent() {
            var self = this
            const files = {}
            if (this.response?.files) {
                for (const file of this.response.files) {
                    files[file.name] = file.content ? file.content : ""
                }
            }
            return files
        },
        tabItems() {
            let tabs = [
                {
                    name: "Audit",
                    id: "id1",
                    slot: "audit",
                    hide: this.isCSV ? false : true,
                    loading: this.auditLoading,
                },
                {
                    name: "Mapping Config",
                    id: "id2",
                    slot: "mapper",
                    loading: this.mapperLoadingStatus == 'running',
                },
                {
                    name: "Advisor",
                    id: "id3",
                    slot: "advisor",
                    loading: this.advisorLoadingStatus == 'running',
                },
            ]
            if (this.graphResponse) {
                var self = this
                tabs.push({
                    name: "Graph",
                    id: "id4",
                    slot: "graph",
                    onClick: () => {
                        nextTick().then(() => {
                            self.drawGraph(self.graphResponse)
                        })
                    }
                })
            }


            return tabs
        },
        getCurrentMapperStatus() {
            if (this.mapperStatus) return `${this.mapperStatus}...`;
            else if (this.mapperLoadingStatus == 'running') return "Loading...";
            else return this.mapperStatusList?.length > 0 ? `Done - ${this.mapperStatusList.length} steps` : "Done";
        },
        getCurrentAdvisorStatus() {
            if (this.advisorStatus) return `${this.advisorStatus}...`;
            else if (this.advisorLoadingStatus == 'running') return "Loading...";
            else return this.advisorStatusList?.length > 0 ? `Done - ${this.advisorStatusList.length} steps` : "Done";
        }
    },
    methods: {
        ...mapActions({
            showModal: "showModal",
            fetchOutputParser: "amdp/fetchOutputParser",
            createChat: "amdp/createChat",
            updateChatTitle: "amdp/updateChatTitle",
            upsertChatMessage: "amdp/upsertChatMessage",
        }),
        untruncateJson,
        getAgentStatusIcon,
        currentStatus,
        async submitAMA() {
            this.loading = true;
            this.submitted = true;
            // Clear all error states
            this.auditError = "";
            this.mapperError = "";
            this.advisorError = "";
            this.response = "";
            this.auditReport = "";
            this.advisorResponse = "";
            this.outputParserData = null;
            this.mapperMessageId = null;
            this.resourceId = `chat_${uuidv4()}`; // new resourceId for the chat

            this.mapperStatus = null;
            this.mapperStatusList = [];
            this.advisorStatus = null;
            this.advisorStatusList = [];
            this.mapperLoadingStatus = 'waiting';
            this.advisorLoadingStatus = 'waiting';

            // create new chat
            await this.createChat({ 
                feature: "map_my_data",
                create_chat_payload: {
                    resourceId: this.resourceId,
                    cid: this.currentClient.cid,
                }
            }).catch((error) => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error('Failed to create chat:', err);
                this.$store.dispatch('addToast', {
                    type: 'error',
                    message: `Error creating new chat: ${err}`,
                });
            });

            // handle create job button visibility
            this.showCreateJobButton = this.mode == "mapper";

            // handle base64 encode for avro/parquet files
            if (this.fileType == "avro" || this.fileType == "parquet") {
                let payload = { limit: 10000 }; // fetch at least the 1st 10K rows
                if (this.fileData.localFile) {
                    const base64Data = await this.convertFileToBase64(this.fileData.localFile)
                    this.fileData.fileContent = base64Data;
                    payload.file_content = base64Data;
                    payload.file_type = this.fileType;
                } else {
                    payload.cid = this.$store.state.currentClient.cid;
                    payload.sandbox_name = this.fileData.sandbox;
                    payload.path = this.fileData.s3Path;
                }
                await this.fetchOutputParser(payload).then((response) => {
                    this.outputParserData = response.data ? response.data : [];
                }).catch(error => {
                    let err = error.response ? error.response.data.error : error.message;
                    let toastoptions = {
                        message: err || "Something went wrong while parsing avro/parquet file. Please contact support.",
                        type: "error"
                    }
                    this.$store.dispatch("addToast", toastoptions);
                    return;
                })
            }

            await this.executeAudit()
            await this.executeMapData()
            await this.executeDataAdvisor()
            this.loading = false;
        },
        async executeAudit() {
            this.auditLoading = true;
            this.auditReport = null;
            this.auditError = ""; // Clear any previous errors
            const endpoint = `${this.$store.state.agentsUrl}/v1/data_audit`;
            const agentsQp = getAgentsRequestQueryParams(this.$store.state);

            try {
                let payload = {
                    security_phrase: "strawberry123",
                    cid: this.$store.state.currentClient.cid,
                    file_type: this.fileType
                }
                if (this.fileData.s3Path && this.fileData.presignedUrl) {
                    // payload.presigned_url = this.fileData.presignedUrl
                    payload.presigned_url = this.fileData.presignedUrl.endsWith("\n") ? this.fileData.presignedUrl.slice(0, -1) : this.fileData.presignedUrl;
                } else {
                    payload.content = this.fileData.fileContent
                }

                // replace parsed output parser data for processing avro/parquet files
                if (this.fileType == "avro" || this.fileType == "parquet") {
                    payload.content = JSON.stringify(this.outputParserData)
                }

                const response = await axios.post(endpoint, payload, {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "text/event-stream",
                        "Authorization": `Bearer ${this.$store.state.accessToken}`
                    },
                    params: agentsQp,
                });

                if (response.data?.markdown_report) {
                    this.auditReport = response.data.markdown_report;
                    console.log(this.auditReport);
                }
            } catch (error) {
                console.error("Error executing audit:", error);

                let err = error.response?.data.error ? error.response.data.error : error.message;
                if (error.response?.status === 401) {
                    this.auditError = "Authentication failed. Please check your credentials and try again.";
                } else if (error.response?.status === 422) {
                    this.auditError = `Failed to execute audit.\nError: ${err}`;
                    this.auditErrorClass = "warning"
                } else {
                    this.auditError = `Error: ${err || "Failed to execute audit"}`;
                }
            } finally {
                this.auditLoading = false;
            }
        },
        async executeMapData() {
            this.mapperLoadingStatus = "running";
            this.response = null;
            this.activeTab = "";
            this.mapperError = ""; // Clear any previous errors
            let tempActiveTab = "";
            this.mapperStatus = null;

            const self = this;

            if (this.eventSource) {
                this.eventSource.close();
            }

            // create user message to chat
            let user_message = {
                "role": "user",
                "content": {
                    "audit_report": this.auditReport,
                    "file_type": this.fileType,
                    "file_content": this.fileData.fileContent,
                    "custom_instructions": this.custom_instructions.trim(),
                    "custom_schema": this.custom_schema.trim(),
                    "mode": this.mode,
                },
                "timestamp": new Date().toISOString(),
            }
            await this.upsertChatMessage({
                feature: "map_my_data",
                resourceId: this.resourceId,
                message_payload: {
                    "messages": [user_message]
                },
            }).catch((error) => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error('Failed to create user message:', err);
                this.$store.dispatch('addToast', {
                    type: 'error',
                    message: `Error creating user message: ${err}`,
                });
            });

            try {
                let endpoint = `${this.$store.state.agentsUrl}/v1/map_my_data`;
                endpoint = mergeAgentsQueryIntoUrl(endpoint, this.$store.state);

                let payload = {
                    aqfer_portal: true,
                    cid: this.$store.state.currentClient.cid,
                    audit_report: this.auditReport,
                    file_type: this.fileType,
                    custom_instructions: this.custom_instructions.trim(),
                    custom_schema: this.custom_schema,
                    mode: this.mode,
                }
                if (this.fileData.s3Path && this.fileData.presignedUrl) {
                    // payload.presigned_url = this.fileData.presignedUrl
                    payload.presigned_url = this.fileData.presignedUrl.endsWith("\n") ? this.fileData.presignedUrl.slice(0, -1) : this.fileData.presignedUrl;
                } else {
                    payload.message = this.fileData.fileContent
                }

                // send parsed output parser data for avro/parquet files
                if (this.fileType == "avro" || this.fileType == "parquet") {
                    payload.parsed_message = this.outputParserData
                }

                const response = await fetch(endpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "text/event-stream",
                        "Authorization": `Bearer ${this.$store.state.accessToken}`
                    },
                    body: JSON.stringify(payload),
                });

                // Check for 401 status code
                if (response.status === 401) {
                    this.mapperError = "Authentication failed. Please check your credentials and try again.";
                    throw new Error("Authentication failed (401)");
                }

                // Check for other error status codes
                if (!response.ok) {
                    this.mapperError = `Server returned error: ${response.status} ${response.statusText}`;
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let forever = true;

                while (forever) {
                    const { value, done } = await reader.read();
                    if (done) {
                        console.log("Stream complete");
                        break;
                    }

                    buffer += decoder.decode(value, { stream: true });

                    const lines = buffer.split('\n\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {

                        const eventMatch = line.match(/^event: (.+)$/m);
                        const dataMatch = line.match(/^data: (.+)$/m);

                        if (eventMatch && dataMatch) {
                            const eventType = eventMatch[1];
                            const dataStr = dataMatch[1];

                            console.log('Event type:', eventType, 'Data:', dataStr);

                            if (eventType == 'message') {
                                try {
                                    const data = JSON.parse(this.untruncateJson(dataStr));
                                    if (data && typeof data === 'object' && (data.text_response || data.files)) {
                                        this.response = data;
                                        // active tab name needs to be fully tokenized before it is set
                                        if (!this.activeTab && data.files && data.files[0].name) {
                                            if (tempActiveTab == data.files[0].name)
                                                this.activeTab = tempActiveTab;
                                            else
                                                tempActiveTab = data.files[0].name;
                                        }
                                    }
                                } catch (e) {
                                    console.error('Error in message chunk:', e);
                                }
                            } else if (eventType == 'status') {
                                try {
                                    let parsedData = JSON.parse(dataStr);
                                    let label = parsedData.label;
                                    let detail = parsedData.detail || '';
                                    let timestamp = parsedData.timestamp || new Date().toISOString().replace(/T/, ' ').replace(/Z/, '');

                                    this.mapperStatus = label;
                                    this.mapperStatusList.push({ label, detail, timestamp });
                                } catch (e) {
                                    console.error('Error in status chunk:', e);
                                }
                            }
                        }
                    }
                }

                // update chat title
                await this.updateChatTitle({
                    feature: "map_my_data",
                    resourceId: this.resourceId,
                    message_payload: {
                        "messages": [user_message],
                    },
                }).catch((error) => {
                    let err = error.response?.data.error ? error.response.data.error : error.message;
                    console.error('Failed to update chat title:', err);
                });

                // create agent message to chat
                let agent_message = {
                    "role": "assistant",
                    "content": this.response,
                    "timestamp": new Date().toISOString(),
                }
                await this.upsertChatMessage({
                    feature: "map_my_data",
                    resourceId: this.resourceId,
                    message_payload: {
                        "messages": [agent_message]
                    },
                }).then((response) => {
                    console.log("Created agent message:", response.data);
                    const created_messages = response.data;
                    this.mapperMessageId = created_messages && created_messages.length > 0 ? created_messages[0].messageId : null;
                }).catch((error) => {
                    console.log("Created agent message(error):", error);
                    let err = error.response?.data.error ? error.response.data.error : error.message;
                    console.error('Failed to create agent message:', err);
                    this.$store.dispatch('addToast', {
                        type: 'error',
                        message: `Error creating agent message: ${err}`,
                    });
                });

            } catch (error) {
                console.error("Error in SSE connection:", error.response?.data?.error || error.message);
                // Only set error if not already set (like for 401 errors)
                if (!this.mapperError) {
                    this.mapperError = error.message || "An error occurred during mapping execution";
                }
            } finally {
                this.mapperLoadingStatus = 'done';
                this.mapperStatus = null;
            }
        },
        async executeDataAdvisor() {
            this.advisorLoadingStatus = "running";
            var endpoint = mergeAgentsQueryIntoUrl(
                `${this.$store.state.agentsUrl}/v1/map_my_data/data_advisor`,
                this.$store.state
            );
            this.advisorError = ""; // Clear any previous errors
            const self = this;
            this.advisorStatus = null;

            try {
                let payload = {
                    cid: this.$store.state.currentClient.cid,
                    data_audit: this.auditReport, // Assuming auditReport is available
                    text_response: this.textResponse, // Assuming textResponse is available
                    file_type: this.fileType,
                    custom_instructions: this.custom_instructions.trim()
                }
                if (this.fileData.s3Path && this.fileData.presignedUrl) {
                    // payload.presigned_url = this.fileData.presignedUrl
                    payload.presigned_url = this.fileData.presignedUrl.endsWith("\n") ? this.fileData.presignedUrl.slice(0, -1) : this.fileData.presignedUrl;
                } else {
                    payload.message = this.fileData.fileContent
                }

                // send parsed output parser data for avro/parquet files
                if (this.fileType == "avro" || this.fileType == "parquet") {
                    payload.parsed_message = this.outputParserData
                }

                const response = await fetch(endpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "text/event-stream",
                        "Authorization": `Bearer ${this.$store.state.accessToken}`
                    },
                    body: JSON.stringify(payload),
                });

                // Check for 401 status code
                if (response.status === 401) {
                    this.advisorError = "Authentication failed. Please check your credentials and try again.";
                    throw new Error("Authentication failed (401)");
                }

                // Check for other error status codes
                if (!response.ok) {
                    this.advisorError = `Server returned error: ${response.status} ${response.statusText}`;
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                const processText = async ({ done, value }) => {
                    if (done) {
                        console.log("Stream complete");
                        return;
                    }

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        const eventMatch = line.match(/^event: (.+)$/m);
                        const dataMatch = line.match(/^data: (.+)$/m);

                        if (eventMatch && dataMatch) {
                            const eventType = eventMatch[1];
                            const dataStr = dataMatch[1];

                            console.log('Event type:', eventType, 'Data:', dataStr);

                            if (eventType == 'message') {
                                try {
                                    const data = JSON.parse(this.untruncateJson(dataStr));
                                    // Update component state with the received data
                                    if (data && typeof data == "object") {
                                        if (data.advisor_response) {
                                            self.advisorResponse = data.advisor_response
                                        }
                                        if (data.graph_response) {
                                            self.graphResponse = data.graph_response;
                                        }
                                    }
                                } catch (e) {
                                    console.error('Error parsing advisor SSE message data:', e);
                                }
                            } else if (eventType == 'status') {
                                try {
                                    let parsedData = JSON.parse(dataStr);
                                    let label = parsedData.label;
                                    let detail = parsedData.detail || '';
                                    let timestamp = parsedData.timestamp || new Date().toISOString().replace(/T/, ' ').replace(/Z/, '');

                                    self.advisorStatus = label;
                                    self.advisorStatusList.push({ label, detail, timestamp });
                                } catch (e) {
                                    console.error('Error parsing advisor SSE status data:', e);
                                }
                            }
                        }
                    }

                    return reader.read().then(processText);
                };

                await reader.read().then(processText);

                // update agent message with data advisor and graph response
                let agent_response = JSON.parse(JSON.stringify(this.response));
                agent_response.advisor_response = self.advisorResponse;
                agent_response.graph_response = self.graphResponse;
                let agent_message = {
                    "role": "assistant",
                    "content": agent_response,
                    "timestamp": new Date().toISOString(),
                    "messageId": this.mapperMessageId, // messageId of the agent message to be updated
                }
                await this.upsertChatMessage({
                    feature: "map_my_data",
                    resourceId: this.resourceId,
                    message_payload: {
                        "messages": [agent_message]
                    },
                    method: "PUT",
                }).catch((error) => {
                    let err = error.response?.data.error ? error.response.data.error : error.message;
                    console.error('Failed to update agent message:', err);
                    this.$store.dispatch('addToast', {
                        type: 'error',
                        message: `Error updating agent message: ${err}`,
                    });
                });
            } catch (error) {
                console.error("Error in SSE connection:", error);
                // advisorError is already set for 401 errors
                if (!this.advisorError) {
                    this.advisorError = error.message || "An error occurred during data advisor execution";
                }
            } finally {
                this.advisorLoadingStatus = "done";
                if (this.graphResponse) {
                    console.log('drawing graph')
                    console.log(this.graphResponse)
                    this.drawGraph(this.graphResponse)
                }
                this.advisorStatus = null;
            }
        },

        drawGraph(data) {
            const width = this.$refs.graphContainer.clientWidth;
            const height = 600;

            // Remove old SVG if it exists
            d3.select(this.$refs.graphContainer).select("svg").remove();

            // Create new SVG with zoom/pan support
            const svg = d3.select(this.$refs.graphContainer)
                .append("svg")
                .attr("width", width)
                .attr("height", height)
                .call(
                    d3.zoom()
                        .scaleExtent([0.5, 5]) // Min zoom 50%, max zoom 500%
                        .on("zoom", (event) => {
                            g.attr("transform", event.transform);
                        })
                );

            // Add a group element for panning/zooming
            const g = svg.append("g");
            this.simulation = d3.forceSimulation(data.nodes)
                .force("link", d3.forceLink(data.edges).id(d => d.id).distance(50))
                .force("charge", d3.forceManyBody().strength(-200))
                .force("center", d3.forceCenter(width / 2, height / 2));

            // Create edges (links)
            const link = g.selectAll(".link")
                .data(data.edges)
                .enter()
                .append("line")
                .attr("stroke", "#999")
                .attr("stroke-width", 1.5)
                .attr("stroke-opacity", 0.6);

            // Create node groups
            const node = g.selectAll(".node")
                .data(data.nodes)
                .enter()
                .append("g")
                .attr("class", "node")
                .call(
                    d3.drag()
                        .on("start", (event, d) => {
                            if (!event.active) this.simulation.alphaTarget(0.3).restart();
                            d.fx = d.x;
                            d.fy = d.y;
                        })
                        .on("drag", (event, d) => {
                            d.fx = event.x;
                            d.fy = event.y;
                        })
                        .on("end", (event, d) => {
                            if (!event.active) this.simulation.alphaTarget(0);
                            d.fx = null;
                            d.fy = null;
                        })
                );

            // Add circles for nodes
            node.append("circle")
                .attr("stroke", this.$store.state.darkMode ? "#1a1d23" : "white") // Border color
                .attr("stroke-width", 1.5) // Border thickness
                .attr("r", d => { // Circle radius
                    if (d.type === "prsn") return 15;
                    if (d.type === "org" || d.type === "hh") return 20;
                    return 12;
                })
                .attr("fill", d => { // Circle colour
                    if (d.type === "prsn") return "blue";
                    if (d.type === "ck") return "orange";
                    if (d.type === "statid") return "green";
                    if (d.type === "ip") return "purple";
                    if (d.type === "org" || d.type === "hh") return "red";
                    return "gray";
                });

            // Add icons inside the circles
            node.append("text")
                .attr("class", "fa")  // Font Awesome class for styling
                .attr("font-family", "FontAwesome")
                .attr("text-anchor", "middle")

                //.attr("x", d => (d.type === "prsn" || d.type === "cookie" || d.type === "statid" ? -6 : -4)) // Adjust horizontal position
                .attr("y", 5)  // Adjust vertical position
                .attr("x", 0)
                .attr("fill", this.$store.state.darkMode ? "#1a1d23" : "white")
                .attr("font-size", d => (d.type === "prsn" ? "14px" : d.type === "org" ? "18px" : d.type === "statid" ? "11px" : "13px"))
                .text(d => {
                    if (d.type === "prsn") return "\uf007"; // person_icon
                    if (d.type === "ck") return "\uf564"; // cookie icon
                    if (d.type === "statid") return "\uf390"; // statid icon
                    if (d.type === "ip") return "\uf6ff"; // network icon
                    if (d.type === "hh") return "\uf015"; // house icon
                    if (d.type === "org") return "\ue533"; // org icon
                    return "\u003f"; // unknown icon
                });

            // Tooltip for nodes
            node.on("mouseover", (event, d) => {
                const tooltip = document.createElement("div");
                tooltip.innerHTML = `<strong>Type: ${d.type}</strong><br>Domain: ${d.domain}<br>Key: ${d.key}`;
                tooltip.className = "graph-tooltip";
                tooltip.style.left = event.pageX + 5 + "px";
                tooltip.style.top = event.pageY + 5 + "px";
                document.body.appendChild(tooltip);
            });
            node.on("mouseout", () => {
                document.querySelectorAll(".graph-tooltip").forEach(tooltip => tooltip.remove());
            });

            // Update positions on simulation tick
            this.simulation.on("tick", () => {
                link
                    .attr("x1", d => d.source.x)
                    .attr("y1", d => d.source.y)
                    .attr("x2", d => d.target.x)
                    .attr("y2", d => d.target.y);
                node.attr("transform", d => `translate(${d.x},${d.y})`);
            });
        },
        handleFileDataUpdate(newFileData) {
            this.fileData = newFileData;
            this.fileType = this.getFileType(newFileData)
        },
        convertFileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();

                reader.onload = () => {
                    // reader.result looks like: "data:application/octet-stream;base64,AAAABBBBCCCC..."
                    const base64 = reader.result.split(',')[1];
                    resolve(base64); // returns only the Base64 part
                };
                reader.onerror = reject;

                // Reads the file and encodes it as a Base64 data URL
                reader.readAsDataURL(file);
            });
        },
        getFileType(fileData) {
            const fileName = fileData?.localFile ? fileData.localFile.name : fileData?.s3Path ? fileData.s3Path : "";
            return fileName.endsWith(".csv")
                ? "csv"
                : fileName.endsWith(".json")
                    ? "json"
                    : fileName.endsWith(".avro")
                        ? "avro"
                        : fileName.endsWith(".parquet")
                            ? "parquet"
                            : "";
        },
    },
    beforeMount() {
        this.$emit("setChat", null);
    },
    beforeUnmount() {
        if (this.eventSource) {
            this.eventSource.close();
        }
    },
    emits: ["setChat"]
};
</script>

<style lang="scss" scoped>
@use '../../theme/scss/_user-variables.scss' as *;

@keyframes shine {
    to {
        background-position-x: -200%;
    }
}

.loading-content-card {
    margin: 0px;
    background: #fff;
    border-radius: 5px;

    .image,
    h2,
    p {
        background: #eee;
        background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
        border-radius: 5px;
        background-size: 200% 100%;
        animation: 1.5s shine linear infinite;
    }

    .content {
        padding: 15px;
    }

    .image {
        height: 200px;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    h2 {
        height: 30px;
    }

    p {
        height: 70px;
    }
}

.loading-content-card-dark {
    margin: 0px;
    background: $dark-card-bg;
    border-radius: 5px;

    .image,
    h2,
    p {
        background: $dark-border-color;
        background: linear-gradient(110deg, $dark-elevated-bg 8%, $dark-border-color 18%, $dark-elevated-bg 33%);
        border-radius: 5px;
        background-size: 200% 100%;
        animation: 1.5s shine linear infinite;
    }

    .content {
        padding: 15px;
    }

    .image {
        height: 200px;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    h2 {
        height: 30px;
    }

    p {
        height: 70px;
    }
}

.markdown-content {
    h2 {
        border-bottom: 1px solid black;
    }

    h3 {
        padding: 10px;
        background-color: #EDF2F9;
    }

    table {

        td,
        th {
            padding: 0.5em;
        }
    }

}

.custom-class {
    width: 100%;
    overflow-x: scroll;
}

// .dropdown-ellipses {
//     color: #6E84A3 !important;
//     font-size: 1.1625rem !important;
// }

// .dropdown-ellipses:hover {
//     color: #000000 !important;
// }

.form-group {
    margin-bottom: 0rem;
}

::v-deep(.form-check-input) {
    background-color: #93bdf0 !important;

    &:checked {
        background-color: #007bff !important;
    }
}

.graph-tooltip {
    position: absolute;
    z-index: 1080;
    background-color: rgba(236, 240, 246, 0.95);
    backdrop-filter: blur(4px);
    color: var(--bs-dark);
    padding: 12px 14px;
    border-radius: 4px;
    font-size: 0.825rem;
    
    /* Tighter line-height for compact metadata */
    line-height: 1.4; 
    
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.25), 0 4px 6px -4px rgba(0, 0, 0, 0.25);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-left: 4px solid #3b82f6;
    min-width: 180px;
    transition: opacity 0.15s ease-in-out;
    pointer-events: none;

    strong {
        display: block;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #60a5fa;
        
        /* Reduced spacing around the divider line */
        margin-bottom: 10px; 
        padding-bottom: 10px;
        border-bottom: 1px solid rgb(199, 199, 199);
    }

    /* THE FIX: Catch the <br> right after the header and strip its layout presence */
    strong + br {
        display: none;
    }
}

.mapper-status, .advisor-status {
    display: flex;
    align-items: center;
    gap: 8px;
}

.status-text {
    font-size: 13px;
}

.border-bottom {
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
}
.border-bottom:last-child {
  border-bottom: 0 !important;
}

/* When collapsed, hide the UP arrow for mapper status */
.mapper-status.collapsed .mapper-collapse-arrow-up {
    display: none !important;
}

/* When expanded (not collapsed), hide the DOWN arrow for mapper status */
.mapper-status:not(.collapsed) .mapper-collapse-arrow-down {
    display: none !important;
}

/* When collapsed, hide the UP arrow for advisor status */
.advisor-status.collapsed .advisor-collapse-arrow-up {
    display: none !important;
}

/* When expanded (not collapsed), hide the DOWN arrow for advisor status */
.advisor-status:not(.collapsed) .advisor-collapse-arrow-down {
    display: none !important;
}

#advisorStatusCollapse, #mapperStatusCollapse {
    max-height: 300px;
    overflow-y: scroll;
}

.custom-instr.collapsed .custom-collapse-arrow-up {
    display: none !important;
}

.custom-instr:not(.collapsed) .custom-collapse-arrow-down {
    display: none !important;
}

.custom-instr-container {
    border: 1px solid #CBD5E1;
    border-radius: 7px;
    background-color: #F8FAFC;
    font-size: 0.9rem;
}

#customConfigCollapse {
    overflow: hidden;
    background-color: #fff;
    border-top: 1px solid #E2E8F0;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
}

.custom-config-badge {
    background-color: rgba(0, 217, 127, 0.2);
    color: #009f60;
    border: 1px solid #009f60;
    font-weight: 600;
    font-size: 0.7rem;
    padding: 0.15rem 0.6rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
}
</style>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .markdown-content table tbody tr td {
        border-color: $dark-border-color !important;
    }

    .custom-instr-container {
        border: 1px solid $dark-border-color;
        background-color: $dark-elevated-bg;
    }

    #customConfigCollapse {
        background-color: $dark-card-bg;
        border-top: 1px solid $dark-border-color;
    }

    .custom-config-badge {
        background-color: rgba(0, 217, 127, 0.2);
        color: #00bf73;
        border: 1px solid #00bf73;
        font-weight: 600;
        font-size: 0.7rem;
        padding: 0.15rem 0.6rem;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
    }

    .graph-tooltip {
        background-color: $dark-border-color !important;
        color: #f1f5f9;
        
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.25), 0 4px 6px -4px rgba(0, 0, 0, 0.25);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-left: 4px solid #3b82f6;

        strong {
            color: #60a5fa;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
    }
}
</style>